import MarkdownIt from 'markdown-it';

export interface IMarkdownInlineTextNode {
  type: 'text';
  content: string;
  bold: boolean;
  italic: boolean;
}

export interface IMarkdownLineBreakNode {
  type: 'lineBreak';
}

export type IMarkdownInlineNode = IMarkdownInlineTextNode | IMarkdownLineBreakNode;

export interface IMarkdownParagraphBlock {
  type: 'paragraph';
  nodes: IMarkdownInlineNode[];
}

export interface IMarkdownBulletListBlock {
  type: 'bulletList';
  items: IMarkdownInlineNode[][];
}

export type IMarkdownBlock = IMarkdownParagraphBlock | IMarkdownBulletListBlock;

interface IInlineMarks {
  bold: boolean;
  italic: boolean;
}

type MarkdownToken = {
  type: string;
  content: string;
  children?: MarkdownToken[];
};

const DEFAULT_MARKS: IInlineMarks = {
  bold: false,
  italic: false,
};

const markdownParser = new MarkdownIt({
  html: false,
  linkify: false,
  typographer: false,
});

const parseInlineTokens = (
  tokens: MarkdownToken[] | undefined,
  marks: IInlineMarks = DEFAULT_MARKS,
): IMarkdownInlineNode[] => {
  if (!tokens || tokens.length === 0) {
    return [];
  }

  const nodes: IMarkdownInlineNode[] = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];

    if (token.type === 'text' || token.type === 'code_inline') {
      nodes.push({
        type: 'text',
        content: token.content,
        bold: marks.bold,
        italic: marks.italic,
      });
      continue;
    }

    if (token.type === 'softbreak') {
      nodes.push({ type: 'lineBreak' });
      continue;
    }

    if (token.type === 'strong_open' || token.type === 'em_open') {
      const closeType = token.type === 'strong_open' ? 'strong_close' : 'em_close';
      let depth = 1;
      let closeIndex = index;

      for (let cursor = index + 1; cursor < tokens.length; cursor += 1) {
        if (tokens[cursor].type === token.type) {
          depth += 1;
        } else if (tokens[cursor].type === closeType) {
          depth -= 1;
          if (depth === 0) {
            closeIndex = cursor;
            break;
          }
        }
      }

      const nextMarks: IInlineMarks = {
        bold: token.type === 'strong_open' ? true : marks.bold,
        italic: token.type === 'em_open' ? true : marks.italic,
      };

      nodes.push(...parseInlineTokens(tokens.slice(index + 1, closeIndex), nextMarks));

      index = closeIndex;
    }
  }

  return nodes;
};

const findClosingToken = (
  tokens: MarkdownToken[],
  startIndex: number,
  openType: string,
  closeType: string,
): number => {
  let depth = 1;

  for (let index = startIndex + 1; index < tokens.length; index += 1) {
    if (tokens[index].type === openType) {
      depth += 1;
    } else if (tokens[index].type === closeType) {
      depth -= 1;
      if (depth === 0) {
        return index;
      }
    }
  }

  return startIndex;
};

const collectListItemNodes = (
  tokens: MarkdownToken[],
  startIndex: number,
  endIndex: number,
): IMarkdownInlineNode[] => {
  const itemNodes: IMarkdownInlineNode[] = [];

  for (let index = startIndex; index <= endIndex; index += 1) {
    const token = tokens[index];

    if (token.type !== 'inline' || !token.children) {
      continue;
    }

    if (itemNodes.length > 0) {
      itemNodes.push({ type: 'lineBreak' });
    }

    itemNodes.push(...parseInlineTokens(token.children));
  }

  return itemNodes;
};

export const parseMarkdownBlocks = (content: string): IMarkdownBlock[] => {
  const normalizedContent = content ?? '';
  const tokens = markdownParser.parse(normalizedContent, {}) as MarkdownToken[];
  const blocks: IMarkdownBlock[] = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];

    if (token.type === 'paragraph_open') {
      const inlineToken = tokens[index + 1];
      const nodes =
        inlineToken?.type === 'inline' && inlineToken.children
          ? parseInlineTokens(inlineToken.children)
          : [];

      blocks.push({
        type: 'paragraph',
        nodes,
      });
      continue;
    }

    if (token.type === 'bullet_list_open') {
      const listCloseIndex = findClosingToken(
        tokens,
        index,
        'bullet_list_open',
        'bullet_list_close',
      );

      const items: IMarkdownInlineNode[][] = [];

      for (let cursor = index + 1; cursor < listCloseIndex; cursor += 1) {
        if (tokens[cursor].type !== 'list_item_open') {
          continue;
        }

        const listItemCloseIndex = findClosingToken(
          tokens,
          cursor,
          'list_item_open',
          'list_item_close',
        );

        items.push(collectListItemNodes(tokens, cursor + 1, listItemCloseIndex - 1));

        cursor = listItemCloseIndex;
      }

      blocks.push({
        type: 'bulletList',
        items,
      });

      index = listCloseIndex;
    }
  }

  return blocks;
};
